import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import "./LangSwitch.css";
import flagFr from "./assets/flag-fr.svg";
import flagUs from "./assets/flag-us.svg";
import useLang from "../../i18n/useLang";
import { LANGS, getRouteIdFromPath, pathFor } from "../../i18n/routes";

// English pages target Google US, so English shows the US flag.
const LANG_OPTIONS = {
  fr: { flag: flagFr, name: "Français" },
  en: { flag: flagUs, name: "English" },
};

const LABEL = { fr: "Changer de langue", en: "Change language" };

const Flag = ({ lang }) => (
  <img className="lang-switch__flag" src={LANG_OPTIONS[lang].flag} alt="" width="16" height="11" />
);

/**
 * Shows the current language only. The menu lists the other languages, each
 * linking to the same page in that language. The links stay in the HTML when
 * the menu is closed, so the prerendered pages keep them.
 */
const LangSwitch = () => {
  const lang = useLang();
  const { pathname } = useLocation();
  const routeId = getRouteIdFromPath(pathname) || "home";
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => setOpen(false), [pathname]);

  useEffect(() => {
    if (!open) return undefined;
    const closeOutside = (event) => {
      if (!ref.current?.contains(event.target)) setOpen(false);
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", closeOutside);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOutside);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  return (
    <div className={`lang-switch${open ? " lang-switch--open" : ""}`} ref={ref}>
      <button
        type="button"
        className="lang-switch__current"
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={`${LABEL[lang]} (${LANG_OPTIONS[lang].name})`}
        onClick={() => setOpen((value) => !value)}
      >
        <Flag lang={lang} />
        <span className="lang-switch__code">{lang.toUpperCase()}</span>
        <svg className="lang-switch__chevron" viewBox="0 0 10 6" aria-hidden="true">
          <path d="M1 1l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      </button>
      <ul className="lang-switch__menu">
        {LANGS.filter((l) => l !== lang).map((l) => (
          <li key={l}>
            <Link
              className="lang-switch__option"
              to={pathFor(routeId, l)}
              hrefLang={l}
              lang={l}
              tabIndex={open ? 0 : -1}
            >
              <Flag lang={l} />
              <span>{LANG_OPTIONS[l].name}</span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default LangSwitch;
